<script setup>
import ApikomContentTable from '@frontJs/components/tables/projectManagement/seo/ApikomContentTable.vue';
import ECommerceCategoryContentTable from '@frontJs/components/tables/projectManagement/seo/ECommerceCategoryContentTable.vue';
import ECommerceProductContentTable from '@frontJs/components/tables/projectManagement/seo/ECommerceProductContentTable.vue';
import TenantReportApikomContentTable from '@frontJs/components/tables/projectManagement/seo/tenantReports/ApikomContentTable.vue';
import ProjectManagementLayout from '@frontJs/layouts/ProjectManagementLayout.vue';
import { Head } from '@inertiajs/vue3';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { useRoute } from '@js/utils/route';
import { h, onMounted, ref } from 'vue';

const { tenant, websiteVariants } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    websiteVariants: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const loading = ref(true);

const data = ref({
    ongoing: 0,
    pending: 0,
    apikomStandard: 0,
    apikomPremium: 0,
    categoryStandard: 0,
    categoryPremium: 0,
    productStandard: 0,
    productPremium: 0,
});

function updateData() {
    if (tenant) {
        loading.value = true;

        Promise.all([
            axios.post(
                route('front.projectManagement.seo.postApikomContentCount', {
                    tenant: tenant.id,
                }),
                {
                    ongoing: true,
                },
            ),
            axios.post(
                route('front.projectManagement.seo.postApikomContentCount', {
                    tenant: tenant.id,
                }),
                {
                    pending: true,
                },
            ),
            axios.post(
                route('front.projectManagement.seo.postApikomContentCount', {
                    tenant: tenant.id,
                }),
                {
                    done: true,
                    standard: true,
                },
            ),
            axios.post(
                route('front.projectManagement.seo.postApikomContentCount', {
                    tenant: tenant.id,
                }),
                {
                    done: true,
                    premium: true,
                },
            ),
            ...(tenant.type.value === 'e-commerce'
                ? [
                      axios.post(
                          route(
                              'front.projectManagement.seo.postECommerceCategoryContentsCount',
                              { tenant: tenant.id },
                          ),
                          {
                              standard: true,
                              hideNotDoneSeoApikomContent: true,
                          },
                      ),
                      axios.post(
                          route(
                              'front.projectManagement.seo.postECommerceCategoryContentsCount',
                              { tenant: tenant.id },
                          ),
                          {
                              premium: true,
                              hideNotDoneSeoApikomContent: true,
                          },
                      ),
                      axios.post(
                          route(
                              'front.projectManagement.seo.postECommerceProductContentsCount',
                              {
                                  tenant: tenant.id,
                              },
                          ),
                          {
                              standard: true,
                              hideNotDoneSeoApikomContent: true,
                          },
                      ),
                      axios.post(
                          route(
                              'front.projectManagement.seo.postECommerceProductContentsCount',
                              {
                                  tenant: tenant.id,
                              },
                          ),
                          {
                              premium: true,
                              hideNotDoneSeoApikomContent: true,
                          },
                      ),
                  ]
                : []),
        ])
            .then((responseData) => {
                data.value.ongoing = responseData[0].data.count;
                data.value.pending = responseData[1].data.count;
                data.value.apikomStandard = responseData[2].data.count;
                data.value.apikomPremium = responseData[3].data.count;

                if (tenant.type.value === 'e-commerce') {
                    data.value.categoryStandard = responseData[4].data.count;
                    data.value.categoryPremium = responseData[5].data.count;
                    data.value.productStandard = responseData[6].data.count;
                    data.value.productPremium = responseData[7].data.count;
                }
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                loading.value = false;
            });
    }
}

onMounted(() => {
    if (tenant) {
        updateData();
    }
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
    <Head>
        <title>
            Contenus Apikom - SEO/GEO - Gestion de projet - Datakepler
        </title>
    </Head>

    <PageTitle :tenant-name="tenant ? tenant.name : undefined">
        Gestion de projet - SEO/GEO - Contenus Apikom
    </PageTitle>

    <TabLayout
        v-if="tenant"
        :tabs="{
            ongoing: {
                title: 'À traiter',
                total: data.ongoing,
                content: h(ApikomContentTable, {
                    tenant,
                    websiteVariants,
                    ongoing: true,
                }),
            },
            pending: {
                title: 'En attente',
                total: data.pending,
                content: h(ApikomContentTable, {
                    tenant,
                    websiteVariants,
                    pending: true,
                }),
            },
            standard: {
                title: 'Standard',
                ...(tenant.type.value === 'e-commerce'
                    ? {
                          items: {
                              apikomStandard: {
                                  title: 'Autre',
                                  total: data.apikomStandard,
                                  content: h(ApikomContentTable, {
                                      tenant,
                                      websiteVariants,
                                      standard: true,
                                      done: true,
                                  }),
                              },
                              categoryStandard: {
                                  title: 'Catégorie',
                                  total: data.categoryStandard,
                                  content: h(ECommerceCategoryContentTable, {
                                      tenant,
                                      standard: true,
                                      hideNotDoneSeoApikomContent: true,
                                  }),
                              },
                              productStandard: {
                                  title: 'Produit',
                                  total: data.productStandard,
                                  content: h(ECommerceProductContentTable, {
                                      tenant,
                                      standard: true,
                                      hideNotDoneSeoApikomContent: true,
                                  }),
                              },
                          },
                      }
                    : {
                          total: data.apikomStandard,
                          content: h(ApikomContentTable, {
                              tenant,
                              websiteVariants,
                              standard: true,
                              done: true,
                          }),
                      }),
            },
            premium: {
                title: 'Premium',
                ...(tenant.type.value === 'e-commerce'
                    ? {
                          items: {
                              apikomPremium: {
                                  title: 'Autre',
                                  total: data.apikomPremium,
                                  content: h(ApikomContentTable, {
                                      tenant,
                                      websiteVariants,
                                      premium: true,
                                      done: true,
                                  }),
                              },
                              categoryPremium: {
                                  title: 'Catégorie',
                                  total: data.categoryPremium,
                                  content: h(ECommerceCategoryContentTable, {
                                      tenant,
                                      premium: true,
                                      hideNotDoneSeoApikomContent: true,
                                  }),
                              },
                              productPremium: {
                                  title: 'Produit',
                                  total: data.productPremium,
                                  content: h(ECommerceProductContentTable, {
                                      tenant,
                                      premium: true,
                                      hideNotDoneSeoApikomContent: true,
                                  }),
                              },
                          },
                      }
                    : {
                          total: data.apikomPremium,
                          content: h(ApikomContentTable, {
                              tenant,
                              websiteVariants,
                              premium: true,
                              done: true,
                          }),
                      }),
            },
        }"
        :loading="loading"
    />

    <TabLayout
        v-else
        :tabs="{
            all: { title: 'Tous', content: h(TenantReportApikomContentTable) },
            standard: {
                title: 'Standard',
                content: h(TenantReportApikomContentTable, { standard: true }),
            },
            premium: {
                title: 'Premium',
                content: h(TenantReportApikomContentTable, { premium: true }),
            },
        }"
    />
</template>
